import React, { useRef } from 'react';
import Select, { Props as ReactSelectProps } from 'react-select';
import { customSelectDropdownContainerId } from './CustomSelectDropdownContainer';
import { CustomSelectDropdownOption } from './CustomSelectDropdownOption';

const closeOnScrollThreshold = 20;

export default function CustomSelect<TOption extends CustomSelectOption>(props: CustomSelectProps<TOption>) {
	const scrollOnOpen = useRef<Scroll | nundefined>(null);
	
	const selectProps: ReactSelectProps<TOption, false> = {
		'aria-label': props.ariaLabel,
		onChange: props.onSelectionChange,
		value: props.selection,
		getOptionLabel: o => props.getOptionLabel(o),
		getOptionValue: o => props.getOptionId(o),
		isOptionDisabled: props.isOptionDisabled,
		isMulti: false,
		options: props.options ?? [],
		isSearchable: true,
		isClearable: true,
		menuPosition: 'fixed',
		menuPortalTarget:  document.getElementById(customSelectDropdownContainerId),
		onMenuOpen: () => {
			if (!props.closeOnScrollTargetClassName) return;
			
			const elm = document.querySelector<HTMLElement>(`.${props.closeOnScrollTargetClassName}`);
			if (!elm) return;
			
			scrollOnOpen.current = {
				top: elm.scrollTop ?? 0,
				left: elm.scrollLeft ?? 0
			};
		},
		closeMenuOnScroll: (e) => { 
			const scroll = scrollOnOpen.current;
			if (!scroll || !props.closeOnScrollTargetClassName || !e.target) return false;
			
			const target = !!(e.target as HTMLElement).classList ? e.target as HTMLElement : null;
			if (!target) return false;
			
			if (!target.classList.contains(props.closeOnScrollTargetClassName)) return false;
			
			return Math.abs(target.scrollTop - scroll.top) > closeOnScrollThreshold || Math.abs(target.scrollLeft - scroll.left) > closeOnScrollThreshold;
		},
		components: {
			Option: CustomSelectDropdownOption
		}
	};

	return <Select {...selectProps} />
}

export interface CustomSelectProps<TOption extends CustomSelectOption> {
	selection: TOption | nundefined;
	onSelectionChange: (val: TOption | nundefined) => void;
	options: TOption[] | nundefined;
	getOptionLabel: (option: TOption) => string;
	/**
	 * this is something that react-select needs to track options
	 */
	getOptionId: (option: TOption) => string;
	isOptionDisabled?: (option: TOption) => boolean;
	ariaLabel: string;
	closeOnScrollTargetClassName?: string;
}

export interface CustomSelectOption {
    tooltipText?: string | nundefined;
    descriptionText?: string | nundefined;
}

interface Scroll {
    top: number;
    left: number;
}
